/**
 * validationSchemas.js
 * Yup 기반 입력 검증 스키마 정의 (관리자 프론트엔드)
 * SI-05 Phase 7: React 관리자 시스템 입력 검증 강화
 */

import * as Yup from 'yup';

/**
 * 관리자 인증 관련 검증 스키마
 */
export const adminAuthSchemas = {
  // 관리자 로그인 스키마
  login: Yup.object().shape({
    adminId: Yup.string()
      .required('관리자 ID를 입력해주세요')
      .min(4, '관리자 ID는 최소 4자 이상이어야 합니다')
      .max(20, '관리자 ID는 최대 20자까지 입력 가능합니다')
      .matches(/^[a-zA-Z0-9_]+$/, '관리자 ID는 영문, 숫자, 언더스코어만 사용 가능합니다'),
    password: Yup.string()
      .required('비밀번호를 입력해주세요')
      .min(8, '비밀번호는 최소 8자 이상이어야 합니다')
      .max(50, '비밀번호는 최대 50자까지 입력 가능합니다')
  }),

  // 비밀번호 변경 스키마
  changePassword: Yup.object().shape({
    currentPassword: Yup.string()
      .required('현재 비밀번호를 입력해주세요')
      .min(8, '비밀번호는 최소 8자 이상이어야 합니다'),
    newPassword: Yup.string()
      .required('새 비밀번호를 입력해주세요')
      .min(8, '비밀번호는 최소 8자 이상이어야 합니다')
      .max(50, '비밀번호는 최대 50자까지 입력 가능합니다')
      .matches(
        /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/,
        '비밀번호는 영문, 숫자, 특수문자를 모두 포함해야 합니다'
      )
      .notOneOf([Yup.ref('currentPassword')], '새 비밀번호는 현재 비밀번호와 달라야 합니다'),
    confirmPassword: Yup.string()
      .required('비밀번호 확인을 입력해주세요')
      .oneOf([Yup.ref('newPassword')], '비밀번호가 일치하지 않습니다')
  })
};

/**
 * 사용자 관리 관련 검증 스키마
 */
export const userManagementSchemas = {
  // 사용자 검색 스키마
  userSearch: Yup.object().shape({
    searchType: Yup.string()
      .required('검색 유형을 선택해주세요')
      .oneOf(['name', 'email', 'phone'], '올바른 검색 유형을 선택해주세요'),
    searchKeyword: Yup.string()
      .required('검색어를 입력해주세요')
      .min(2, '검색어는 최소 2자 이상 입력해주세요')
      .max(50, '검색어는 최대 50자까지 입력 가능합니다')
      .matches(/^[가-힣a-zA-Z0-9@._-\s]+$/, '검색어에 특수문자는 사용할 수 없습니다')
  }),

  // 사용자 정보 수정 스키마
  userEdit: Yup.object().shape({
    name: Yup.string()
      .required('이름을 입력해주세요')
      .min(2, '이름은 최소 2자 이상이어야 합니다')
      .max(20, '이름은 최대 20자까지 입력 가능합니다')
      .matches(/^[가-힣a-zA-Z\s]+$/, '이름은 한글, 영문만 사용 가능합니다'),
    email: Yup.string()
      .required('이메일을 입력해주세요')
      .email('올바른 이메일 형식이 아닙니다')
      .max(100, '이메일은 최대 100자까지 입력 가능합니다'),
    phoneNumber: Yup.string()
      .required('전화번호를 입력해주세요')
      .matches(/^01[0-9]{8,9}$/, '올바른 전화번호 형식이 아닙니다'),
    birthDate: Yup.date()
      .required('생년월일을 입력해주세요')
      .max(new Date(), '생년월일은 현재 날짜보다 이전이어야 합니다'),
    gender: Yup.string()
      .required('성별을 선택해주세요')
      .oneOf(['M', 'F'], '올바른 성별을 선택해주세요')
  })
};

/**
 * 의료 데이터 관리 관련 검증 스키마
 */
export const medicalDataSchemas = {
  // 혈당 데이터 검색/필터 스키마
  glucoseFilter: Yup.object().shape({
    userId: Yup.number()
      .nullable()
      .positive('올바른 사용자 ID를 입력해주세요'),
    startDate: Yup.date()
      .nullable()
      .max(new Date(), '시작 날짜는 현재 날짜보다 이전이어야 합니다'),
    endDate: Yup.date()
      .nullable()
      .max(new Date(), '종료 날짜는 현재 날짜보다 이전이어야 합니다')
      .when('startDate', {
        is: (startDate) => startDate,
        then: (schema) => schema.min(Yup.ref('startDate'), '종료 날짜는 시작 날짜보다 이후여야 합니다')
      }),
    minValue: Yup.number()
      .nullable()
      .min(20, '최소값은 20 이상이어야 합니다')
      .max(600, '최소값은 600 이하여야 합니다'),
    maxValue: Yup.number()
      .nullable()
      .min(20, '최대값은 20 이상이어야 합니다')
      .max(600, '최대값은 600 이하여야 합니다')
      .when('minValue', {
        is: (minValue) => minValue,
        then: (schema) => schema.min(Yup.ref('minValue'), '최대값은 최소값보다 커야 합니다')
      })
  }),

  // 혈당 데이터 수정 스키마
  glucoseEdit: Yup.object().shape({
    value: Yup.number()
      .required('혈당 수치를 입력해주세요')
      .min(20, '혈당 수치는 20 이상이어야 합니다')
      .max(600, '혈당 수치는 600 이하여야 합니다')
      .integer('혈당 수치는 정수여야 합니다'),
    measurementTime: Yup.date()
      .required('측정 시간을 입력해주세요')
      .max(new Date(), '측정 시간은 현재 시간보다 이전이어야 합니다'),
    mealType: Yup.string()
      .required('식사 구분을 선택해주세요')
      .oneOf(['BEFORE_MEAL', 'AFTER_MEAL', 'BEDTIME', 'OTHER'], '올바른 식사 구분을 선택해주세요'),
    memo: Yup.string()
      .nullable()
      .max(200, '메모는 최대 200자까지 입력 가능합니다')
      .matches(/^[가-힣a-zA-Z0-9\s.,!?-]*$/, '메모에는 특수문자를 사용할 수 없습니다')
  })
};

/**
 * 시스템 설정 관련 검증 스키마
 */
export const systemSettingsSchemas = {
  // 공지사항 작성/수정 스키마
  notice: Yup.object().shape({
    title: Yup.string()
      .required('제목을 입력해주세요')
      .min(5, '제목은 최소 5자 이상이어야 합니다')
      .max(100, '제목은 최대 100자까지 입력 가능합니다')
      .matches(/^[가-힣a-zA-Z0-9\s.,!?()-]+$/, '제목에 허용되지 않는 문자가 포함되어 있습니다'),
    content: Yup.string()
      .required('내용을 입력해주세요')
      .min(10, '내용은 최소 10자 이상이어야 합니다')
      .max(5000, '내용은 최대 5000자까지 입력 가능합니다'),
    isImportant: Yup.boolean()
      .required('중요 공지 여부를 선택해주세요'),
    isActive: Yup.boolean()
      .required('활성화 여부를 선택해주세요')
  }),

  // FAQ 작성/수정 스키마
  faq: Yup.object().shape({
    question: Yup.string()
      .required('질문을 입력해주세요')
      .min(10, '질문은 최소 10자 이상이어야 합니다')
      .max(200, '질문은 최대 200자까지 입력 가능합니다')
      .matches(/^[가-힣a-zA-Z0-9\s.,!?()-]+$/, '질문에 허용되지 않는 문자가 포함되어 있습니다'),
    answer: Yup.string()
      .required('답변을 입력해주세요')
      .min(10, '답변은 최소 10자 이상이어야 합니다')
      .max(2000, '답변은 최대 2000자까지 입력 가능합니다'),
    category: Yup.string()
      .required('카테고리를 선택해주세요')
      .oneOf(['GENERAL', 'ACCOUNT', 'DEVICE', 'DATA', 'OTHER'], '올바른 카테고리를 선택해주세요'),
    isActive: Yup.boolean()
      .required('활성화 여부를 선택해주세요')
  }),

  // 시스템 알림 설정 스키마
  systemAlert: Yup.object().shape({
    title: Yup.string()
      .required('알림 제목을 입력해주세요')
      .min(5, '제목은 최소 5자 이상이어야 합니다')
      .max(100, '제목은 최대 100자까지 입력 가능합니다'),
    message: Yup.string()
      .required('알림 메시지를 입력해주세요')
      .min(10, '메시지는 최소 10자 이상이어야 합니다')
      .max(500, '메시지는 최대 500자까지 입력 가능합니다'),
    alertType: Yup.string()
      .required('알림 유형을 선택해주세요')
      .oneOf(['INFO', 'WARNING', 'ERROR', 'SUCCESS'], '올바른 알림 유형을 선택해주세요'),
    targetUsers: Yup.string()
      .required('대상 사용자를 선택해주세요')
      .oneOf(['ALL', 'ACTIVE', 'INACTIVE', 'SPECIFIC'], '올바른 대상을 선택해주세요'),
    scheduledTime: Yup.date()
      .nullable()
      .min(new Date(), '예약 시간은 현재 시간 이후여야 합니다')
  })
};

/**
 * 보안 정책 관련 검증 스키마
 */
export const securityPolicySchemas = {
  // 비밀번호 정책 스키마
  passwordPolicy: Yup.object().shape({
    minLength: Yup.number()
      .required('최소 길이를 입력해주세요')
      .min(8, '최소 길이는 8자 이상이어야 합니다')
      .max(50, '최소 길이는 50자 이하여야 합니다')
      .integer('최소 길이는 정수여야 합니다'),
    maxLength: Yup.number()
      .required('최대 길이를 입력해주세요')
      .min(Yup.ref('minLength'), '최대 길이는 최소 길이보다 커야 합니다')
      .max(100, '최대 길이는 100자 이하여야 합니다')
      .integer('최대 길이는 정수여야 합니다'),
    requireUppercase: Yup.boolean()
      .required('대문자 포함 여부를 선택해주세요'),
    requireLowercase: Yup.boolean()
      .required('소문자 포함 여부를 선택해주세요'),
    requireNumbers: Yup.boolean()
      .required('숫자 포함 여부를 선택해주세요'),
    requireSpecialChars: Yup.boolean()
      .required('특수문자 포함 여부를 선택해주세요'),
    expirationDays: Yup.number()
      .required('만료 기간을 입력해주세요')
      .min(30, '만료 기간은 최소 30일 이상이어야 합니다')
      .max(365, '만료 기간은 최대 365일 이하여야 합니다')
      .integer('만료 기간은 정수여야 합니다')
  }),

  // 계정 잠금 정책 스키마
  lockoutPolicy: Yup.object().shape({
    maxFailedAttempts: Yup.number()
      .required('최대 실패 횟수를 입력해주세요')
      .min(3, '최대 실패 횟수는 3회 이상이어야 합니다')
      .max(10, '최대 실패 횟수는 10회 이하여야 합니다')
      .integer('최대 실패 횟수는 정수여야 합니다'),
    lockoutDurationMinutes: Yup.number()
      .required('잠금 지속 시간을 입력해주세요')
      .min(5, '잠금 지속 시간은 최소 5분 이상이어야 합니다')
      .max(1440, '잠금 지속 시간은 최대 1440분(24시간) 이하여야 합니다')
      .integer('잠금 지속 시간은 정수여야 합니다'),
    resetPeriodMinutes: Yup.number()
      .required('초기화 기간을 입력해주세요')
      .min(30, '초기화 기간은 최소 30분 이상이어야 합니다')
      .max(1440, '초기화 기간은 최대 1440분(24시간) 이하여야 합니다')
      .integer('초기화 기간은 정수여야 합니다')
  })
};

/**
 * 파일 업로드 관련 검증 스키마
 */
export const fileUploadSchemas = {
  // 이미지 업로드 스키마
  imageUpload: Yup.object().shape({
    file: Yup.mixed()
      .required('파일을 선택해주세요')
      .test('fileSize', '파일 크기는 5MB 이하여야 합니다', (value) => {
        return value && value.size <= 5 * 1024 * 1024;
      })
      .test('fileType', '이미지 파일만 업로드 가능합니다', (value) => {
        return value && ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
      }),
    description: Yup.string()
      .nullable()
      .max(200, '설명은 최대 200자까지 입력 가능합니다')
  }),

  // 문서 업로드 스키마
  documentUpload: Yup.object().shape({
    file: Yup.mixed()
      .required('파일을 선택해주세요')
      .test('fileSize', '파일 크기는 10MB 이하여야 합니다', (value) => {
        return value && value.size <= 10 * 1024 * 1024;
      })
      .test('fileType', 'PDF, DOC, DOCX 파일만 업로드 가능합니다', (value) => {
        return value && [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ].includes(value.type);
      }),
    title: Yup.string()
      .required('문서 제목을 입력해주세요')
      .min(5, '제목은 최소 5자 이상이어야 합니다')
      .max(100, '제목은 최대 100자까지 입력 가능합니다'),
    category: Yup.string()
      .required('카테고리를 선택해주세요')
      .oneOf(['MANUAL', 'POLICY', 'REPORT', 'OTHER'], '올바른 카테고리를 선택해주세요')
  })
};

/**
 * 공통 검증 함수들
 */
export const commonValidations = {
  // 한국어 이름 검증
  koreanName: Yup.string()
    .matches(/^[가-힣]+$/, '한글 이름만 입력 가능합니다'),

  // 영문 이름 검증
  englishName: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, '영문 이름만 입력 가능합니다'),

  // 한국 전화번호 검증
  koreanPhoneNumber: Yup.string()
    .matches(/^01[0-9]{8,9}$/, '올바른 전화번호 형식이 아닙니다'),

  // 이메일 검증 (강화된 버전)
  strictEmail: Yup.string()
    .email('올바른 이메일 형식이 아닙니다')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      '올바른 이메일 형식이 아닙니다'
    ),

  // 안전한 텍스트 (XSS 방지)
  safeText: Yup.string()
    .matches(/^[^<>'"&]*$/, '허용되지 않는 문자가 포함되어 있습니다'),

  // 숫자만 허용
  numbersOnly: Yup.string()
    .matches(/^[0-9]+$/, '숫자만 입력 가능합니다'),

  // 영문자와 숫자만 허용
  alphanumeric: Yup.string()
    .matches(/^[a-zA-Z0-9]+$/, '영문자와 숫자만 입력 가능합니다')
};
